import { RadioGroupField } from '@/components/shared/RadioGroup';
import { validatePhone } from '@/helpers/phoneHelper';
import { FC, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { PhoneNumberField, PhoneNumberValue } from '../../components/form/PhoneNumberField';
import { TextField } from '../../components/form/TextInput';
import { Checkbox } from '../../components/shared/checkboxes/Checkbox';
import { CheckboxGroupField } from '../../components/shared/checkboxes/CheckboxGroup';
import { validateEmail } from '../../helpers/Utility';
import { isAdmin, Permissions, useAuthenticationService } from '../../services/authentication/authentication-service';
import { Feature } from '../../services/backstage/Feature';
import { ReadEmployee, ReadRole, useGetMeQuery, useGetRolesQuery } from '../../services/gql/graphql.generated';
import { useLocationService } from '../../services/location/LocationService';
import strings from './team.translations';
import { TeamMemberFormFields } from './teamMember/AddSingleTeamMemberForm';
import { roleSelectOptions } from './teamMember/overview/TeamFormCommon';
import { EditTeamMemberFields } from './teamMember/overview/TeamMemberDetailsEditForm';
import { useAllLocations } from '@/services/useLocations';

interface TeamMemberAddOrEditFormContentProps {
  addMode: boolean;
  employee?: ReadEmployee;
  methods: UseFormReturn<TeamMemberFormFields> | UseFormReturn<EditTeamMemberFields>;
}

export const TeamMemberAddOrEditFormContent: FC<TeamMemberAddOrEditFormContentProps> = ({
  addMode,
  employee,
  methods
}) => {
  const [permission] = useAuthenticationService(state => [state.user?.profile.permission]);
  const [checked, setChecked] = useState<boolean>(true);

  const [phoneOrEmailRequiredError, setPhoneOrEmailRequiredError] = useState<boolean>(false);

  const [{ data }] = useGetMeQuery();
  const isMe = employee?.identityId === data!.myEmployee!.identityId; //Shouldn't be able to modify your own role
  const canEditRole = permission?.includes(Permissions.EmployeeUpdate) && !isMe;

  const pauseRolesQuery = !canEditRole || !data;

  const [{ data: roleData }] = useGetRolesQuery({
    pause: pauseRolesQuery
  });
  const roles: ReadRole[] = roleData?.roles ?? [];

  const locationList = useLocationService(state => state.locationList);
  const activeLocationList = useMemo(() => {
    return locationList.filter(l => l.active);
  }, [locationList]);

  const mappedLocations = useLocationService(state => state.mappedLocations);
  const setMappedLocations = useLocationService(state => state.setMappedLocations);

  const role = useAuthenticationService(state => state.role);
  const allLocations = useAllLocations();
  const locations = isAdmin(role) ? allLocations : activeLocationList;

  useEffect(() => {
    setMappedLocations(
      locations.map(location => {
        return {
          key: String(location.id),
          name: location.name,
          checked: Boolean(employee?.locations?.find(restaurant => restaurant.id === location.id)),
          descriptionMarkup: <p>{location.name}</p>
        };
      })
    );
  }, [locations, employee, setMappedLocations]);

  const firstAndLastNameFields = (
    <div className="flex flex-row gap-2">
      <TextField
        id="firstName"
        name="firstName"
        label={strings.common.firstName}
        placeholder={!addMode ? undefined : strings.addTeamMemberForm.firstNamePlaceholder}
        validationMessage={
          !addMode
            ? strings.editTeamMember.fistNameRequired
            : strings.common.mandatory(strings.addTeamMemberForm.firstName)
        }
        required
      />
      <TextField
        id="lastName"
        name="lastName"
        label={strings.common.lastName}
        placeholder={!addMode ? undefined : strings.addTeamMemberForm.lastNamePlaceholder}
        validationMessage={
          !addMode
            ? strings.editTeamMember.lastNameRequired
            : strings.common.mandatory(strings.addTeamMemberForm.lastName)
        }
        required
      />
    </div>
  );

  const roleFields = (
    <>
      {(addMode || canEditRole) && (
        <RadioGroupField
          id="role"
          className="w-full"
          fieldSetClasses={`flex flex-col md:flex-row md:flex-wrap space-y-4 md:space-y-0 focus:bg-red-500 whitespace-nowrap`}
          labelClasses={'border rounded-md'}
          name="role"
          label={strings.role}
          title={strings.role}
          required
          validationMessage={strings.common.mandatory(strings.role)}
          noOptionsString={strings.addTeamMemberForm.noOptionsString}
          //@ts-ignore
          items={roleSelectOptions(roles)}
        />
      )}
    </>
  );

  const emailAndPhoneNumberFields = (
    <Feature name="detailsEdit">
      <TextField
        name="email"
        id="email"
        label={strings.common.email}
        validator={(email: string) => {
          return validateEmail(email);
        }}
        customValidatorMessage={strings.addTeamMemberForm.invalidEmail}
      />
      <PhoneNumberField
        id="phoneNumber"
        name="phoneNumber"
        label={strings.common.phoneNumber}
        validate={(value: PhoneNumberValue) => {
          const phoneOrEmailRequired = !(value.number || methods.getValues().email);
          setPhoneOrEmailRequiredError(phoneOrEmailRequired);

          if (!value.number) return true;

          return validatePhone(value.number, value.region) || strings.addTeamMemberForm.invalidPhone;
        }}
      />
      {phoneOrEmailRequiredError && (
        <div className="mb-4 text-red-500">{strings.editTeamMember.phoneNumberOrEmailRequired}</div>
      )}
    </Feature>
  );

  const locationFields = (
    <Feature name="devices">
      {locations.length > 1 && (
        <CheckboxGroupField
          fieldSetClasses="grid grid-flow-row grid-cols-1 md:grid-cols-3"
          name="locationIds"
          title={strings.locations}
          //@ts-ignore
          onChange={(ids: string[]) => {
            mappedLocations.forEach(location => {
              if (ids.includes(location.key)) {
                location.checked = true;
              } else {
                location.checked = false;
              }
            });

            setMappedLocations([...mappedLocations]);
          }}
          items={mappedLocations}
          required
          validationMessage={strings.common.mandatory(strings.location)}
        />
      )}
    </Feature>
  );

  const sendAccountInformation = (
    <div className="flex flex-row p-2 pl-4 rounded-lg bg-secondary-std gap-2 mb-4">
      <Checkbox name="sendRegistration" onChange={() => setChecked(!checked)} checked={checked} />
      <div className="flex flex-col align-start p-2 rounded-lg bg-secondary-std gap-2">
        <p className="">{strings.addTeamMemberForm.toggleSendAccountInformation}</p>
        <p className="italic">{strings.addTeamMemberForm.sendAccountInformation}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {firstAndLastNameFields}
      {roleFields}
      {emailAndPhoneNumberFields}
      {locationFields}
      {addMode && sendAccountInformation}
    </div>
  );
};
